import { useState, useEffect } from 'react';
import { Save, Globe, Mail, Bell, Shield, CreditCard } from 'lucide-react';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { toast } from 'sonner';
import { supabase } from '../integrations/supabase/client';

const keys = {
  app_name: 'app_name',
  app_url: 'app_url',
  timezone: 'timezone',
  support_email: 'support_email',
  sales_email: 'sales_email',
  smtp_server: 'smtp_server',
  session_max_hours: 'session_max_hours',
  login_attempts: 'login_attempts',
} as const;

const NOTIFICATION_KEYS = [
  { key: 'notif_new_client', label: 'Novo cliente cadastrado' },
  { key: 'notif_payment', label: 'Pagamento recebido' },
  { key: 'notif_critical_alert', label: 'Alerta crítico do sistema' },
  { key: 'notif_limit_80', label: 'Cliente atingiu 80% do limite' },
  { key: 'notif_daily_summary', label: 'Resumo diário por email' },
] as const;

function getStr(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
}

function parseBool(v: unknown): boolean {
  if (v === true || v === 'true' || v === '1') return true;
  return false;
}

export function Settings() {
  const { settings, isLoading, updateSetting } = useSystemSettings();
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    notif_new_client: true,
    notif_payment: true,
    notif_critical_alert: true,
    notif_limit_80: false,
    notif_daily_summary: true,
  });
  const [form, setForm] = useState({
    app_name: '',
    app_url: '',
    timezone: 'America/Sao_Paulo',
    support_email: '',
    sales_email: '',
    smtp_server: '',
    session_max_hours: '24',
    login_attempts: '5',
    asaas_api_key: '',
    asaas_environment: 'sandbox',
    evolution_url: '',
    instagram_url: '',
  });

  useEffect(() => {
    if (settings && typeof settings === 'object') {
      setForm((prev) => ({
        ...prev,
        app_name: getStr(settings[keys.app_name]) || 'INNFLOW Master',
        app_url: getStr(settings[keys.app_url]) || 'https://admin.innflow.com',
        timezone: getStr(settings[keys.timezone]) || 'America/Sao_Paulo',
        support_email: getStr(settings[keys.support_email]) || 'suporte@innflow.com',
        sales_email: getStr(settings[keys.sales_email]) || 'vendas@innflow.com',
        smtp_server: getStr(settings[keys.smtp_server]) || 'smtp.resend.com',
        session_max_hours: getStr(settings[keys.session_max_hours]) || '24',
        login_attempts: getStr(settings[keys.login_attempts]) || '5',
        asaas_api_key: getStr(settings['asaas_api_key']) || '',
        asaas_environment: getStr(settings['asaas_environment']) || 'sandbox',
        evolution_url: getStr(settings['evolution_url']) || '',
        instagram_url: getStr(settings['instagram_url']) || '',
      }));
      setNotifications((prev) => {
        const next = { ...prev };
        NOTIFICATION_KEYS.forEach(({ key }) => {
          if (settings[key] !== undefined) next[key] = parseBool(settings[key]);
        });
        return next;
      });
    }
  }, [settings]);

  const handleToggleNotification = async (key: string) => {
    const current = notifications[key] ?? false;
    const next = !current;
    setNotifications((prev) => ({ ...prev, [key]: next }));
    try {
      await updateSetting.mutateAsync({ key, value: next ? 'true' : 'false' });
      toast.success('Notificação atualizada');
    } catch {
      setNotifications((prev) => ({ ...prev, [key]: current }));
      toast.error('Erro ao atualizar');
    }
  };

  const handleSave = async () => {
    try {
      await Promise.all([
        updateSetting.mutateAsync({ key: keys.app_name, value: form.app_name }),
        updateSetting.mutateAsync({ key: keys.app_url, value: form.app_url }),
        updateSetting.mutateAsync({ key: keys.timezone, value: form.timezone }),
        updateSetting.mutateAsync({ key: keys.support_email, value: form.support_email }),
        updateSetting.mutateAsync({ key: keys.sales_email, value: form.sales_email }),
        updateSetting.mutateAsync({ key: keys.smtp_server, value: form.smtp_server }),
        updateSetting.mutateAsync({ key: keys.session_max_hours, value: form.session_max_hours }),
        updateSetting.mutateAsync({ key: keys.login_attempts, value: form.login_attempts }),
        updateSetting.mutateAsync({ key: 'asaas_api_key', value: form.asaas_api_key }),
        updateSetting.mutateAsync({ key: 'asaas_environment', value: form.asaas_environment }),
        updateSetting.mutateAsync({ key: 'evolution_url', value: form.evolution_url }),
        updateSetting.mutateAsync({ key: 'instagram_url', value: form.instagram_url }),
      ]);
      toast.success('Configurações salvas');
    } catch {
      toast.error('Erro ao salvar');
    }
  };

  const handleTestASAAS = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/sync-asaas-payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Conexão ASAAS OK!');
      } else {
        toast.error(data.error || 'Erro na conexão ASAAS');
      }
    } catch (error) {
      toast.error('Falha ao testar conexão');
    }
  };

  const handleSyncASAAS = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/sync-asaas-payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Sincronizado: ${data.synced_payments || 0} pagamentos, ${data.synced_subscriptions || 0} assinaturas`);
      } else {
        toast.error(data.error || 'Erro na sincronização');
      }
    } catch (error) {
      toast.error('Falha ao sincronizar');
    }
  };

  const handleTestEvolution = async () => {
    if (!form.evolution_url) {
      toast.error('URL vazia. Preencha a URL primeiro.');
      return;
    }

    try {
      const response = await fetch(`${form.evolution_url}/health`);
      if (response.ok) {
        toast.success('Evolution API conectada - Health check OK');
      } else {
        toast.error('Evolution API não respondeu corretamente');
      }
    } catch (error) {
      toast.error('Não foi possível conectar à Evolution API');
    }
  };

  const handleTestInstagram = async () => {
    if (!form.instagram_url) {
      toast.error('URL vazia. Preencha a URL primeiro.');
      return;
    }

    try {
      const response = await fetch(`${form.instagram_url}/health`);
      if (response.ok) {
        toast.success('Instagram Service conectado - Health check OK');
      } else {
        toast.error('Instagram Service não respondeu corretamente');
      }
    } catch (error) {
      toast.error('Não foi possível conectar ao Instagram Service');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Configurações</h1>
        <p className="text-slate-400">Configurações globais do sistema (persistidas em system_settings)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Configurações Gerais</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Nome do Sistema</label>
              <input
                type="text"
                value={form.app_name}
                onChange={(e) => setForm((p) => ({ ...p, app_name: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">URL do Sistema</label>
              <input
                type="text"
                value={form.app_url}
                onChange={(e) => setForm((p) => ({ ...p, app_url: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Timezone</label>
              <select
                value={form.timezone}
                onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              >
                <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Europe/London">Europe/London</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Mail className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Configurações de Email</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email de Suporte</label>
              <input
                type="email"
                value={form.support_email}
                onChange={(e) => setForm((p) => ({ ...p, support_email: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email de Vendas</label>
              <input
                type="email"
                value={form.sales_email}
                onChange={(e) => setForm((p) => ({ ...p, sales_email: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">SMTP Server</label>
              <input
                type="text"
                value={form.smtp_server}
                onChange={(e) => setForm((p) => ({ ...p, smtp_server: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Notificações</h3>
          </div>

          <div className="space-y-4">
            {NOTIFICATION_KEYS.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-300">{label}</span>
                <button
                  type="button"
                  onClick={() => handleToggleNotification(key)}
                  disabled={updateSetting.isPending}
                  className={`w-12 h-7 rounded-full relative transition-colors ${notifications[key] !== false ? 'bg-red-500' : 'bg-white/10'}`}
                >
                  <span className={`absolute w-5 h-5 rounded-full bg-white top-1 transition-all ${notifications[key] !== false ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-red-500/20">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Segurança</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Sessão Máxima (horas)</label>
              <input
                type="number"
                value={form.session_max_hours}
                onChange={(e) => setForm((p) => ({ ...p, session_max_hours: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Tentativas de Login</label>
              <input
                type="number"
                value={form.login_attempts}
                onChange={(e) => setForm((p) => ({ ...p, login_attempts: e.target.value }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>

        {/* Integrações */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <CreditCard className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Integrações</h3>
          </div>

          <div className="space-y-6">
            {/* ASAAS */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-300">ASAAS - Pagamentos</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">API Key</label>
                  <input
                    type="password"
                    value={form.asaas_api_key}
                    onChange={(e) => setForm((p) => ({ ...p, asaas_api_key: e.target.value }))}
                    placeholder="$aact_xxxxxxxxxxxxxxxx"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Encontre em: ASAAS Dashboard → Integrações → Chave de API
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleTestASAAS}
                    className="px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5 transition-colors"
                  >
                    Testar Conexão
                  </button>
                  <button
                    onClick={handleSyncASAAS}
                    className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    Sincronizar Dados
                  </button>
                </div>
              </div>
            </div>

            {/* Evolution API */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-slate-300">Evolution API - WhatsApp</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">URL Evolution API</label>
                  <input
                    type="url"
                    value={form.evolution_url}
                    onChange={(e) => setForm((p) => ({ ...p, evolution_url: e.target.value }))}
                    placeholder="https://evolution-xxxxx.zeabur.app"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>
                <button
                  onClick={handleTestEvolution}
                  className="px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5 transition-colors"
                >
                  Testar Conexão
                </button>
              </div>
            </div>

            {/* Instagram Service */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-slate-300">Instagram Service</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">URL Instagram Service</label>
                  <input
                    type="url"
                    value={form.instagram_url}
                    onChange={(e) => setForm((p) => ({ ...p, instagram_url: e.target.value }))}
                    placeholder="https://instagram-xxxxx.zeabur.app"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>
                <button
                  onClick={handleTestInstagram}
                  className="px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5 transition-colors"
                >
                  Testar Conexão
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={updateSetting.isPending}
          className="flex items-center gap-2 px-6 py-3 admin-gradient rounded-lg text-white font-medium disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          Salvar Configurações
        </button>
      </div>
    </div>
  );
}

